import { useCallback, useEffect, useState } from "react";

import { getApiErrorStatus } from "../../lib/api/apiError";
import {
  buildOrderInfoPayload,
  mapMasterPlacedOrder,
  mapMasterReservation,
  mapPlacedOrder,
  mapReservation,
} from "./orderMappers";
import {
  placeMasterOrder,
  placeOrder,
  reserveMasterOrder,
  reserveOrder,
} from "./orderService";
import { RESERVATION_TTL_SECONDS, secondsRemaining } from "./reservationClock";
import {
  clearPendingReservation,
  readPendingReservation,
  savePendingReservation,
} from "./reservationStore";

const PLACE_ATTEMPTS = 3;
const PLACE_RETRY_DELAY_MS = 2500;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isMissingReservation = (error) => getApiErrorStatus(error) === 404;

const initialReservation = () => {
  const pending = readPendingReservation();

  if (!pending?.reservation?.pendingOrderId) return { reservation: null, reservedAt: null };

  const left = secondsRemaining({
    reservedAt: pending.reservedAt,
    expiresAt: pending.reservation.expiresAt,
  });

  if (left <= 0) {
    clearPendingReservation();

    return { reservation: null, reservedAt: null };
  }

  return { reservation: pending.reservation, reservedAt: pending.reservedAt };
};

const useCheckoutOrder = ({ carts, deliveryTypeIdForCart, address, couponCode } = {}) => {
  const [{ reservation: initReservation, reservedAt: initReservedAt }] = useState(initialReservation);
  const [phase, setPhase] = useState(initReservation ? "reserved" : "idle");
  const [reservation, setReservation] = useState(initReservation);
  const [reservedAt, setReservedAt] = useState(initReservedAt);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [error, setError] = useState(null);
  const [placedOrder, setPlacedOrder] = useState(null);

  useEffect(() => {
    if (!reservation) {
      setSecondsLeft(null);

      return undefined;
    }

    const tick = () =>
      setSecondsLeft(secondsRemaining({ reservedAt, expiresAt: reservation.expiresAt }));

    tick();
    const id = setInterval(tick, 1000);

    return () => clearInterval(id);
  }, [reservation, reservedAt]);

  const dropReservation = useCallback(() => {
    clearPendingReservation();
    setReservation(null);
    setReservedAt(null);
    setPlacedOrder(null);
    setError(null);
    setPhase("idle");
  }, []);

  const reserve = useCallback(async () => {
    const list = carts || [];
    if (!list.length) return { status: "skipped" };

    const kind = list.length > 1 ? "master" : "single";

    setPhase("reserving");
    setError(null);

    try {
      const payloads = list.map((cart) =>
        buildOrderInfoPayload({
          cartId: cart.cartId,
          deliveryTypeId: deliveryTypeIdForCart(cart.cartId),
          deliveryAddress: address,
          couponCode,
        })
      );

      const mapped =
        kind === "master"
          ? mapMasterReservation(await reserveMasterOrder({ orders: payloads }))
          : mapReservation(await reserveOrder(payloads[0]));

      const reservedAtNow = new Date().toISOString();
      const withKind = { ...mapped, kind };

      setReservation(withKind);
      setReservedAt(reservedAtNow);
      setPhase("reserved");
      savePendingReservation({ reservation: withKind, reservedAt: reservedAtNow });

      return { status: "reserved", reservation: withKind };
    } catch (err) {
      setError(err);
      setPhase("idle");

      return { status: "error", error: err };
    }
  }, [carts, deliveryTypeIdForCart, address, couponCode]);

  const place = useCallback(
    async (paymentPayload, { stopOn, reservationOverride } = {}) => {
      const current = reservationOverride ?? reservation;

      if (!current?.pendingOrderId) return { status: "missing-reservation" };

      setPhase("placing");

      for (let attempt = 0; attempt < PLACE_ATTEMPTS; attempt += 1) {
        try {
          const dto =
            current.kind === "master"
              ? await placeMasterOrder({
                  masterPendingOrderId: current.pendingOrderId,
                  ...paymentPayload,
                })
              : await placeOrder({ pendingOrderId: current.pendingOrderId, ...paymentPayload });

          const mapped =
            current.kind === "master" ? mapMasterPlacedOrder(dto) : mapPlacedOrder(dto);

          clearPendingReservation();
          setPlacedOrder(mapped);
          setPhase("placed");

          return { status: "placed", order: mapped };
        } catch (err) {
          if (isMissingReservation(err)) {
            dropReservation();

            return { status: "expired", error: err };
          }

          if (stopOn?.(err)) {
            setPhase("reserved");

            return { status: "stopped", error: err };
          }

          if (attempt === PLACE_ATTEMPTS - 1) {
            setError(err);
            setPhase("reserved");

            return { status: "error", error: err };
          }

          await wait(PLACE_RETRY_DELAY_MS);
        }
      }

      return { status: "error" };
    },
    [reservation, dropReservation]
  );

  return {
    phase,
    setPhase,
    reservation,
    secondsLeft,
    ttlSeconds: RESERVATION_TTL_SECONDS,
    error,
    placedOrder,
    reserve,
    place,
    dropReservation,
  };
};

export default useCheckoutOrder;
